import AccountCircle from '@mui/icons-material/AccountCircle';
import {
    Avatar,
    Box,
    Button,
    IconButton,
    Menu,
    Tooltip,
    type PopoverOrigin,
    type Theme,
    useMediaQuery,
} from '@mui/material';
import {
    Logout,
    UserMenuContextProvider,
    useAuthProvider,
    useGetIdentity,
    useTranslate,
} from 'react-admin';
import { useCallback, useMemo, useState } from 'react';

const anchorOrigin: PopoverOrigin = {
    vertical: 'bottom',
    horizontal: 'right',
};

const transformOrigin: PopoverOrigin = {
    vertical: 'top',
    horizontal: 'right',
};

export function AdminUserMenu() {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const translate = useTranslate();
    const { identity, isPending } = useGetIdentity();
    const authProvider = useAuthProvider();
    const isLargeEnough = useMediaQuery<Theme>((theme) => theme.breakpoints.up('sm'));

    const handleClose = useCallback(() => setAnchorEl(null), []);
    const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    }, []);

    const context = useMemo(() => ({ onClose: handleClose }), [handleClose]);
    const open = Boolean(anchorEl);
    const fullName = identity?.fullName?.trim();
    const avatar = identity?.avatar;
    const label = translate('ra.auth.user_menu', { _: 'Profile' });
    const menuContent = authProvider ? <Logout /> : null;

    if (!menuContent) {
        return null;
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            {isLargeEnough && !isPending && fullName ? (
                <Tooltip title={fullName}>
                    <Button
                        aria-controls={open ? 'admin-user-menu' : undefined}
                        aria-expanded={open ? 'true' : undefined}
                        aria-haspopup="menu"
                        aria-label={label}
                        color="inherit"
                        onClick={handleMenuOpen}
                        startIcon={
                            avatar ? (
                                <Avatar
                                    alt={fullName}
                                    src={avatar}
                                    sx={{ width: 24, height: 24 }}
                                />
                            ) : (
                                <AccountCircle />
                            )
                        }
                        variant="text"
                        sx={{
                            minWidth: 0,
                            maxWidth: {
                                sm: 'min(240px, calc(100vw - 200px))',
                                md: 'min(280px, calc(100vw - 240px))',
                            },
                            ml: 0.5,
                            px: 1,
                            color: '#ffffff',
                            textTransform: 'none',
                            overflow: 'hidden',
                            '& .MuiButton-startIcon': {
                                flexShrink: 0,
                                mr: 1,
                                color: '#ffffff',
                            },
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                display: 'block',
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {fullName}
                        </Box>
                    </Button>
                </Tooltip>
            ) : (
                <Tooltip title={label}>
                    <IconButton
                        aria-controls={open ? 'admin-user-menu' : undefined}
                        aria-expanded={open ? 'true' : undefined}
                        aria-haspopup="menu"
                        aria-label={label}
                        color="inherit"
                        onClick={handleMenuOpen}
                        sx={{ ml: 0.5, color: '#ffffff' }}
                    >
                        {!isPending && avatar ? (
                            <Avatar
                                alt={fullName || label}
                                src={avatar}
                                sx={{ width: 24, height: 24 }}
                            />
                        ) : (
                            <AccountCircle />
                        )}
                    </IconButton>
                </Tooltip>
            )}
            <UserMenuContextProvider value={context}>
                <Menu
                    id="admin-user-menu"
                    disableScrollLock
                    anchorEl={anchorEl}
                    anchorOrigin={anchorOrigin}
                    transformOrigin={transformOrigin}
                    open={open}
                    onClose={handleClose}
                    slotProps={{
                        paper: {
                            sx: {
                                minWidth: 160,
                                maxWidth: 'calc(100vw - 16px)',
                            },
                        },
                    }}
                >
                    {menuContent}
                </Menu>
            </UserMenuContextProvider>
        </Box>
    );
}